import React, { useMemo, useRef, useCallback, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { AssignmentStatus, AssignmentCategory, MasteryPacingColor, ResourceType, Entity, MasteryPacing, SkillResourceProvider } from "@/enums";
import { Assignment, Student, ReTeachFocusSkill, ReTeachStudent } from "@/models";
import { useWeeklyPlan, useSkillResources, useReTeachMasteryData, useTargetWeekCurriculumData } from "@/hooks";
import { recommendationRules } from "@/features/planner/services/rules/rolloverRecommendations";
import { RolloverRecommendationAction } from "@/features/planner/enums";
import { RolloverRecommendation } from "@/features/planner/services/rolloverRecommend";
import { selectUserData } from "@/store/slices";
import { getSkillsMetadata } from "@/services/weeklyPlanner/query/getSkillsMetadata";

// Initialize the recommendation engine
const recommender = RolloverRecommendation(recommendationRules);

export interface FollowUpStudentRec extends Student {
  name: string;
  completedAssignments: number;
  totalAssignments: number;
  masteryNow: keyof typeof MasteryPacingColor;
  recommendedAction: RolloverRecommendationAction;
  scenario?: string;
  explanation?: string;
  errorCount: number;
  exposureCount: number;
  fulfillment?: Assignment[];
  masteryMonday?: keyof typeof MasteryPacingColor;
}

export interface FollowUpPresentationRow {
  skillName: string;
  skillActivityId: string;
  displayName: string;
  students: FollowUpStudentRec[];
}

// Helper method to calculate assignment completion percentage
const calculateAssignmentCompletionPercent = (
  studentAssignments: Assignment[]
): number => {
  // Get completed student assignments (numerator)
  const completedStudentAssignments = studentAssignments.filter(assignment =>
    assignment.status === AssignmentStatus.COMPLETE ||
    assignment.status === AssignmentStatus.DONE
  );

  // The denominator should be the total number of student assignments
  // Each student assignment represents one assignment that needs to be completed
  const totalStudentAssignments = studentAssignments.length;

  // Calculate completion percentage
  return totalStudentAssignments > 0
    ? Math.round((completedStudentAssignments.length / totalStudentAssignments) * 100)
      : 0;
};

// Helper method to calculate students ready to move on
const calculateStudentsReadyToMoveOn = (focusSkills: ReTeachFocusSkill[]): number => {
  if (!focusSkills || focusSkills.length === 0) {
    return 0;
  }

  // Get all unique students across all skills
  const allStudents = new Map<string, { studentId: string; studentName: string; skills: string[] }>();

  focusSkills.forEach(skill => {
    skill.students.forEach(student => {
      if (!allStudents.has(student.studentId)) {
        allStudents.set(student.studentId, {
          studentId: student.studentId,
          studentName: student.studentName,
          skills: []
        });
      }
      allStudents.get(student.studentId)!.skills.push(skill.skillId);
    });
  });

  // Check each student's recommendations across all their skills
  let readyToMoveOnCount = 0;

  allStudents.forEach((studentInfo, studentId) => {
    // Get all skills this student is assigned to
    const studentSkills = focusSkills.filter(skill =>
      skill.students.some(student => student.studentId === studentId)
    );

    // Check if student has "Move On" recommendation for ALL their skills
    const allSkillsReady = studentSkills.every(skill => {
      const studentInSkill = skill.students.find(student => student.studentId === studentId);
      return studentInSkill?.recommendedAction === RolloverRecommendationAction.MoveOn;
    });

    if (allSkillsReady) {
      readyToMoveOnCount++;
    }
  });

  return readyToMoveOnCount;
};

// this is for rollover data
export function useReTeachData() {
  const hasShownTable = useRef(false);
  const { data: weeklyPlanData } = useWeeklyPlan();
  const [ isFetchingPrerequisites, setIsFetchingPrerequisites ] = useState(false);
  const [ prerequisiteSkillIds, setPrerequisiteSkillIds ] = useState<string[]>([]);

  // State to track teacher overrides for student recommendations
  const [recommendationOverrides, setRecommendationOverrides] = useState<Map<string, RolloverRecommendationAction>>(new Map());

  // Create a stable reference for recommendation overrides to prevent unnecessary re-renders
  const stableRecommendationOverrides = useMemo(() => recommendationOverrides, [recommendationOverrides.size, JSON.stringify(Array.from(recommendationOverrides.entries()))]);

  // Function to update a student's recommendation
  const updateStudentRecommendation = (studentId: string, skillId: string, action: RolloverRecommendationAction) => {    
    const validActions = Object.values(RolloverRecommendationAction);
    if (!validActions.includes(action)) {
      return;
    }

    const key = `${studentId}-${skillId}`;
    
    setRecommendationOverrides((prev: Map<string, RolloverRecommendationAction>) => {
      const newMap = new Map(prev);
      newMap.set(key, action);
      return newMap;
    });
  };

  // The rollover workflow should be accessible starting on the last day of the school week ("Friday") at noon, and be available to complete, if the user has not already completed it, until some time (noon?) on the first day of the next school week ("Monday")
  // ^ ^ ^ time might still need to be implemented somewhere (usePlannerCompare?) since it was a late requirement
  // Weekly plan slice always gets data for the "current week" (Monday to Friday);
  // Saturdays and Sundays are also treated as if they're in the "current week" by the plan (USER LOCALE DEPENDENT? SERVER DEPENDENT?)
  // During the rollover period(as determined by usePlannerCompare?) FRI-SUN, "last week" should really be the -current weekly plan-
  // During the rollover period MON, "last week" should really be "last week's" weekly plan(new and potentially difficult requirement?)
  // reTeachPlanFrom: the start of the current week (Monday), OR the start of last week (Monday) if today is Monday
  // reTeachPlanToActual: the current day, OR the end of last week(Sunday) if today is Monday
  const { 
    reTeachPlanFrom,
    reTeachPlanToActual
  } = useMemo(() => {    
    // For Saturday (6) and Sunday (7), don't subtract a week - use the current week
    // For Friday (5) after noon, don't subtract a week - use the current week
    // For other days (Monday-Thursday, Friday before noon), subtract 1 week to get the previous week
    const dayOfWeek = moment().isoWeekday();
    const isAfterNoon = moment().hour() >= 12;
    const shouldSubtractWeek = !(dayOfWeek >= 6 || (dayOfWeek === 5 && isAfterNoon)); // Don't subtract week for Saturday (6), Sunday (7), or Friday (5) after noon
    return { 
      reTeachPlanFrom: moment().startOf("isoWeek").startOf('day').subtract(shouldSubtractWeek ? 1 : 0, "week").toDate(),
      reTeachPlanToActual: moment().endOf("isoWeek").endOf('day').subtract(shouldSubtractWeek ? 1 : 0, "week").toDate()
    };
  }, []);

  // Get user's allowed resource providers
  const providers = useSelector(selectUserData).entitlements.resourceProviders || [];
  
  // Skill resources data utility
  const { getResources, isLoading: resourceLoading, error: resourceError } = useSkillResources();
  const {
    curriculumData: reTeachCurriculumData,
    focusSkills: reTeachFocusSkills,
    isLoading: curriculumLoading,
    error: curriculumLoadingError,
    students
  } = useTargetWeekCurriculumData({ dateFrom: reTeachPlanFrom, dateTo: reTeachPlanToActual });

  // Create a stable reference for students
  const stableStudents = useMemo(() => students, [JSON.stringify(students.map(s => s.id))]);
  // Create a stable reference for focus skills
  const stableReTeachFocusSkills = useMemo(() => reTeachFocusSkills, [JSON.stringify(reTeachFocusSkills?.map(s => s.skillId))]);
  
  // Fetch mastery data for Monday and Today
  const { 
    masteryData,
    isLoading: masteryLoading,
    error: masteryLoadingError
  } = useReTeachMasteryData({
    students: stableStudents || [],
    skillIds: stableReTeachFocusSkills?.map(skill => skill.skillId) || [],
    mondayDate: moment(reTeachPlanFrom).format('YYYY-MM-DD'),
  });

  // Create a stable reference for mastery data to prevent unnecessary re-renders
  const stableMasteryData = useMemo(() => masteryData, [JSON.stringify(masteryData)]);

  // Create stable assignments reference to prevent unnecessary recalculations
  const stableAssignments = useMemo(() => weeklyPlanData?.assignments
    // Filter assignments for the re-teach period
    ?.filter(({dateFrom}) => moment(dateFrom)
        .isBetween(
          moment(reTeachPlanFrom),
          moment(reTeachPlanToActual),
          'day',
          '[]'
        )
    )
    // Filter out duplicate assignments
    ?.filter((assignment, _, currentArray) => {
      // Find all assignments with the same lessonPlanId and entityId
      const duplicates = currentArray.filter(({lessonPlanId, entityId}) => lessonPlanId === assignment.lessonPlanId && entityId === assignment.entityId);
      if (duplicates.length === 1) return true; // If this is the only one, keep it
      
      // If there are duplicates, keep only the newest one by createdAt
      const newestDuplicate = duplicates.reduce((newest, current) => new Date(current.createdAt) > new Date(newest.createdAt) ? current : newest);
      
      // Keep this assignment only if it's the newest one
      return assignment.assignmentId === newestDuplicate.assignmentId;
    }) ?? [], 
    [weeklyPlanData?.assignments, reTeachPlanFrom, reTeachPlanToActual]
  );

  // Calculate assignment completion percentage using weeklyPlanData
  const assignmentCompletePercent = useMemo(() => {
      if (!stableAssignments || stableAssignments.length === 0) return 0;
      // Separate main assignments (district/class) and student assignments
      const mainAssignments = stableAssignments.filter(assignment => 
        assignment.entityType === Entity.DISTRICT || assignment.entityType === Entity.CLASS
      );
      const studentAssignments = stableAssignments.filter(assignment => 
        assignment.entityType === Entity.STUDENT
      );
  
      // Calculate completion percentage using the helper method
      return calculateAssignmentCompletionPercent(studentAssignments);
  }, [stableAssignments, reTeachPlanFrom, reTeachPlanToActual]);

  // Simple transformation of focus skills with student recommendations
  const transformedFocusSkills = useMemo(() => {
    if (!stableReTeachFocusSkills || stableReTeachFocusSkills.length === 0) {
      return [];
    }
    return stableReTeachFocusSkills.map((skill, skillIndex) => {
      const curriculumName = skill.curriculumName || skill.curriculumId || "Unknown Curriculum";
      const studentsForSkill: ReTeachStudent[] = stableStudents.map((student: any) => {
        const studentMastery = stableMasteryData[student.id]?.[skill.skillId];
        const mondayStatus = studentMastery?.mondayStatus;
        const currentStatus = studentMastery?.currentStatus;
        const exposureCount = studentMastery?.exposureCount;
        const errorCount = studentMastery?.errorCount;
        const timeLastWeek = 100; // TODO: get this from the assignment data
        const validMastery = (val: any): keyof typeof MasteryPacing => [MasteryPacing.AHEAD_OF_GRADE_LEVEL, MasteryPacing.AT_GRADE_LEVEL, MasteryPacing.BELOW_GRADE_LEVEL].includes(val) ? val : MasteryPacing.NO_DATA;;
        const assignmentCategories = stableAssignments
          .filter((assignment: Assignment) =>
            assignment.entityType === Entity.STUDENT &&
            assignment.entityId === student.id &&
            assignment.lessonPlanId === skill.lessonPlanId
          )
          .map((assignment: Assignment) => assignment.assignmentCategory)
          .flat();

        const studentAssignments = stableAssignments
          .filter((assignment: Assignment) =>
            assignment.entityType === Entity.STUDENT &&
            assignment.entityId === student.id &&
            assignment.lessonPlanId === skill.lessonPlanId
          );
        const {
          completedAssignments,
          incompleteAssignments
        } = studentAssignments.reduce((acc, assignment: Assignment) => {
          assignment.status === AssignmentStatus.COMPLETE || assignment.status === AssignmentStatus.DONE 
            ? acc.completedAssignments.push(assignment) 
              : acc.incompleteAssignments.push(assignment);
          return acc;
        }, { 
          completedAssignments: [] as Assignment[], 
          incompleteAssignments: [] as Assignment[]
        });
        // If there are no assignments at all, consider them "completed" since there's nothing to complete
        // This prevents the recommendation engine from suggesting CompleteAssignments when there are no assignments to complete
        const hasCompletedAssignments = studentAssignments.length === 0 || (incompleteAssignments.length === 0 && completedAssignments.length > 0);
        const skillType = assignmentCategories?.includes(AssignmentCategory.ROLLOVER)
        ? AssignmentCategory.ROLLOVER
          : assignmentCategories?.includes(AssignmentCategory.PREREQUISITE)
            ? AssignmentCategory.PREREQUISITE
              : AssignmentCategory.CORE;

        const studentState = {
          studentId: student.id,
          skillId: skill.skillId,
          skillType,
          startMastery: validMastery(mondayStatus),
          endMastery: validMastery(currentStatus),
          completedAssignments: hasCompletedAssignments,
          totalAssignments: studentAssignments.length,
        };

        const computedRecommendation = recommender.get(studentState);
        const {rule, reason: explanation} = recommender.explain(studentState);

        // CHECK FOR TEACHER OVERRIDE FIRST, then fall back to computed recommendation
        const overrideKey = `${student.id}-${skill.skillId}`;
        const recommendedAction = stableRecommendationOverrides.get(overrideKey) || computedRecommendation || RolloverRecommendationAction.MoveOn;

        return {
          studentName: `${student.firstName} ${student.lastName}`,
          studentId: student.id,
          mondayMasteryStatus: mondayStatus,
          currentMasteryStatus: currentStatus,
          exposureCount: exposureCount,
          errorCount: errorCount,
          timeLastWeek: timeLastWeek,
          recommendedAction,
          hasCompletedAssignments,
          incompleteAssignments,
          completedAssignments,
          assignmentCategory: assignmentCategories,
          assignments: studentAssignments,
          scenario: rule?.scenario,
          explanation,
        } as ReTeachStudent;
      })
      /** 
        * ============= START neutralizer ======================= 
        * Temporary hack: convert first student of first skill to TeacherInstruction
        * Comment/uncomment to toggle this hack
      */
        // .map((student, studentIndex) => (
        //   skillIndex === 0 && studentIndex === 0
        //     ? { ...student, recommendedAction: RolloverRecommendationAction.TeacherInstruction, hasCompletedAssignments: true }
        //       : skillIndex === 0 && studentIndex === 2
        //         ? { ...student, recommendedAction: RolloverRecommendationAction.RecommendPrerequisite, hasCompletedAssignments: true }
        //           : student
        // ));
      /** ============= END neutralizer ======================= */

      // if (skillIndex === 0) console.log(`----====> (useReTeachData) Collecting students for skill ${skill.displayName}`, studentsForSkill);

      return {
        skillName: skill.skillName || skill.displayName || skill.skillId,
        skillId: skill.skillId,
        displayName: skill.displayName || skill.skillName || skill.skillId,
        curriculumName: curriculumName,
        lessonPlanId: skill.lessonPlanId,
        skillType: skill.skillType,
        students: studentsForSkill
      };
    });
  }, [stableReTeachFocusSkills, stableStudents, stableMasteryData, stableAssignments, stableRecommendationOverrides]);

  // Get skill IDs for TeacherInstruction students (including hacked ones)
  const {
    teacherInstructSkillIds,
    prerequisiteSkillIdsToFetch,
    tutorSkillIds,
  } = useMemo(() => {
    const teacherInstructionSkillIds = transformedFocusSkills
      .filter(skill => skill.students.some(student => student.recommendedAction === RolloverRecommendationAction.TeacherInstruction))
      .map(skill => skill.skillId);
    const prerequisiteSkillIdsToFetch = transformedFocusSkills
      .filter(skill => skill.students.some(student => student.recommendedAction === RolloverRecommendationAction.RecommendPrerequisite))
      .map(skill => skill.skillId);
    const tutorSkillIds = transformedFocusSkills
      .filter(skill => skill.students.some(student => student.recommendedAction === RolloverRecommendationAction.AddSkillTutoring))
      .map(skill => skill.skillId);
    return {
      teacherInstructSkillIds: Array.from(new Set(teacherInstructionSkillIds)),
      prerequisiteSkillIdsToFetch: Array.from(new Set(prerequisiteSkillIdsToFetch)),
      tutorSkillIds: Array.from(new Set(tutorSkillIds)),
    };
  }, [transformedFocusSkills]);

  useEffect(() => {
    const fetchPrerequisites = async () => {
      if (prerequisiteSkillIdsToFetch.length > 0 && !isFetchingPrerequisites) {
        setIsFetchingPrerequisites(true);
        try {
          const result = await getSkillsMetadata(prerequisiteSkillIdsToFetch);
          if (result.skillsMetadata) {
            const skillIds: string[] = result.skillsMetadata
              .map(({preReqSkills}: { preReqSkills: string[] }) => preReqSkills?.[0])
              .filter(Boolean);
            setPrerequisiteSkillIds([...new Set(skillIds)]);
          }
        } catch (error: any) {
          console.error('Error fetching prerequisite skill metadata:', error);
        } finally {
          setIsFetchingPrerequisites(false);
        }
      }
    };
    
    fetchPrerequisites();
  }, [ prerequisiteSkillIdsToFetch ]);

  const resourceMap = useMemo(() => {
    const allResourceSkillIds = [
      ...tutorSkillIds,
      ...prerequisiteSkillIds,
      ...teacherInstructSkillIds
    ];
    // Don't make a request for an empty set of skill IDs or if another request is in queue
    if (allResourceSkillIds.length === 0 || resourceLoading) return {};
    // console.log(`----====> (useReTeachData) providers`, providers);
    return getResources(allResourceSkillIds, 1000, { providers: providers.length ? providers : undefined });
    // return getResources(allResourceSkillIds, 1000);
  }, [tutorSkillIds, prerequisiteSkillIds, teacherInstructSkillIds, resourceLoading]);

  // Callback to get filtered resources for a specific skill
  const getFilteredResourcesForSkill = useCallback((skillId: string, action: RolloverRecommendationAction) => {
    // Currently, we're only looking for teacher resources
    const resources = resourceMap[skillId] || [];
    switch (action) {
      case RolloverRecommendationAction.TeacherInstruction:
        return [resources
          .find((resource: any) =>
            resource.resourceType === ResourceType.teacherResource &&
            resource.provider === SkillResourceProvider.AMIRA
          )
        ].filter(Boolean);
      case RolloverRecommendationAction.RecommendPrerequisite:
        return resources
          .filter((resource: any) =>
            resource.tags && 
            resource.tags.includes('SKILLDEFAULT-ASSIGNED')
          );
      case RolloverRecommendationAction.AddSkillTutoring:
        return resources
          .filter((resource: any) => resource.resourceType === ResourceType.skillTutor);
      case RolloverRecommendationAction.NoAction:
      case RolloverRecommendationAction.MoveOn:
      default:
        return [];
    }
  }, [resourceMap]);

  // Reactively Hydrate fulfillments
  const hydratedSkills = useMemo(() => {
    if (!resourceMap) return transformedFocusSkills; // by default, return the unhydrated skills
    return transformedFocusSkills
      .map((skill) => ({
        ...skill,
        students: skill.students
          .map(student => {
            const resources = student.recommendedAction === RolloverRecommendationAction.CompleteAssignments
              ? student.incompleteAssignments 
                : getFilteredResourcesForSkill(skill.skillId, student.recommendedAction ?? RolloverRecommendationAction.NoAction);
            // Either we get the resources, or we get an empty array
            if (resources && resources.length > 0) {
              return {
                ...student,
                fulfillment: resources
              };
            }
            return student;
          })
        }))
    }, 
    [transformedFocusSkills, resourceMap, getFilteredResourcesForSkill]
  );

  const isLoading = useMemo(() => {
    // If we've already shown the table, only show loading for critical loading states (curriculum and mastery)
    // Don't show loading for resources as they can load in the background
    if (hasShownTable.current) {
      return false;
    }

    // For first-time loading, only show loading for actual loading states
    const loading = curriculumLoading || masteryLoading;

    // Once loading is complete, mark the table as shown
    if (!loading) {
      hasShownTable.current = true;
    }

    return loading;
  }, [curriculumLoading, masteryLoading]);

  // Calculate students ready to move on
  const studentsReadyToMoveOn = useMemo(() => calculateStudentsReadyToMoveOn(hydratedSkills as ReTeachFocusSkill[]), [hydratedSkills]);
  const studentsNotReadyToMoveOn = useMemo(() => (stableStudents.length - studentsReadyToMoveOn), [studentsReadyToMoveOn, stableStudents.length]);

  const returnObject = {
    weeklyPlanData,
    dateFrom: reTeachPlanFrom,
    dateTo: reTeachPlanToActual,
    assignmentCompletePercent,
    reTeachCurriculumData,
    reTeachFocusSkills: hydratedSkills,
    studentsReadyToMoveOn,
    studentsNotReadyToMoveOn,
    studentsCount: stableStudents.length,
    isLoading,
    curriculumLoadingError,
    masteryLoadingError,
    resourceLoadingError: resourceError,
    updateStudentRecommendation
  };
  
  return returnObject;
}